import { BoxComponentProps } from '@dtx-company/shared-components/src/types/atoms/box.types'
import { FcSvg, TemplateGeneratorOptions } from '@dtx-company/flowcode-generator-browser/src'
import {
  FlowcodeDownloadOptionMimeType,
  FlowcodeType,
  PageInfoType
} from '@dtx-company/inter-app/src/types/flowcode'
import { FormEvent } from 'react'
import { TemplateCategory } from '@app/common/src/types/placement-template.types'
import { UseFormReturn } from 'react-hook-form'

export interface TemplateCategoryTabsProps extends BoxComponentProps {
  categories?: TemplateCategory[]
  isLoading?: boolean
}

export interface PlacementTemplateTileProps {
  name: string
  imageUrl: string
  selectedSubCategory?: TemplateCategory
}

export type TemplateBaseConfig = Omit<TemplateGeneratorOptions, 'flowcode'>

export type SerializedTemplateBaseConfig = Omit<
  TemplateBaseConfig,
  'logo1' | 'logo2' | 'logo3' | 'logo4'
> & {
  logo1FileBase64?: string
  logo2FileBase64?: string
  logo3FileBase64?: string
  logo4FileBase64?: string
}

export interface TemplateConfigInfo extends TemplateBaseConfig {
  logoCount: number
  textCount: number
}

export interface WirePlacementTemplateConfig {
  url: string
  studioConfigId: string
  templateOptions: TemplateBaseConfig
  imageType: FlowcodeDownloadOptionMimeType
  width?: number
  inchWidth?: number
  density?: number
}

export interface CreatePlacementTemplateDownloadDataVariablesType {
  templateGenerationDetails: string
  flowcodeId: string
  templateId: string
  actionType: CreatePlacementTemplateActionTypes
}

export interface PlacementTemplateProduct {
  id: string
  name: string
  imageUrls: string[]
}

export interface PlacementTemplateConfigGroup {
  id: string
  name: string
  configuration: string
  category: {
    name: string
    id: string
  }
  placeholderAssetFile: {
    url: string
  }
  size: string
  isPrintable: boolean
  isPurchasable: boolean
  isSingleFlowcode: boolean
  products?: PlacementTemplateProduct[]
}

interface TemplatePrintStoreProducts {
  products?: PlacementTemplateProduct[]
}

export interface GetPlacementTemplateConfigGroupResponseType {
  placementTemplateConfigGroup: PlacementTemplateConfigGroup[]
  batch?: FlowcodeType
  templatePrintStoreProducts?: TemplatePrintStoreProducts
}

export interface GetTemplateCategoriesResponseType {
  placementTemplateCategories: {
    pageInfo: PageInfoType
    edges: {
      node: {
        name: string
        id: string
        type: string
        tileDisplay?: string
        isDigital: boolean
        templates?: PlacementTemplateConfigGroup[]
      }
    }[]
    totalCount: string
  }
}

export interface PlacementTemplateConfigBatch {
  nickname?: string
  studioConfig?: {
    id: string
    configuration: string
  }
  activeLink?: {
    redirectValue: string
    shorturl: string
  }
}

export enum PlacementTemplateFormFields {
  TEMPLATE = 'template',
  LOGO1 = 'logo1',
  LOGO2 = 'logo2',
  LOGO3 = 'logo3',
  LOGO4 = 'logo4',
  LOGO1_FILE = 'logo1File',
  LOGO2_FILE = 'logo2File',
  LOGO3_FILE = 'logo3File',
  LOGO4_FILE = 'logo4File',
  TEXT1 = 'text1',
  TEXT2 = 'text2'
}

export interface TemplateBaseConfigWithFiles extends TemplateBaseConfig {
  logo1File?: File
  logo2File?: File
  logo3File?: File
  logo4File?: File
}

export interface PlacementTemplateDetails {
  url: string
  studioConfigId: string
  templateOptions: TemplateBaseConfig
}

export enum PlacementTemplateSubmitButtonState {
  DOWNLOAD = 'download',
  PRINT = 'print',
  EMAIL = 'email'
}

export enum CreatePlacementTemplateActionTypes {
  DOWNLOAD = 'DOWNLOAD',
  PRINT_AT_HOME = 'PRINT_AT_HOME'
}

export interface PlacementTemplateFormState {
  watchAll: TemplateBaseConfigWithFiles
  setValue: UseFormReturn<TemplateBaseConfigWithFiles>['setValue']
  buttonState: PlacementTemplateSubmitButtonState
  placementTemplateButtonDetails: PlacementTemplateButtonDetails[]
  areButtonsDisabled: boolean
  mobile: boolean
  isEmailTemplateModalOpen: boolean
  setEmailTemplateModalClose: () => void
  isDownloadTemplateModalOpen: boolean
  setDownloadTemplateModalClose: () => void
  handleTemplateSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    logoFile: string,
    logoUrl: string
  ) => Promise<void>
  templateIdString: string
  setEmailTemplateModalOpen: () => void
  setDownloadTemplateModalOpen: () => void
  getValues: UseFormReturn<TemplateBaseConfigWithFiles>['getValues']
  handlePrintTemplateClick: () => void
  handleOrderPrintClick: () => Promise<void>
  isPrintButtonLoading: boolean
  reset: UseFormReturn<TemplateBaseConfigWithFiles>['reset']
}

export interface PlacementTemplateButtonDetails {
  buttonCopy: string
  handleOnClick: () => void
  isLoading: boolean
  isPrimary: boolean
  showOnDesktop: boolean
  showOnMobile: boolean
}

export interface GeneratePlacementTemplateWithFilesProps {
  wireConfig: TemplateBaseConfigWithFiles
  studioConfigId: string
  shortUrl: string
  buttonState?: PlacementTemplateSubmitButtonState
  imageType?: FlowcodeDownloadOptionMimeType
  token: string | undefined
}

export interface EmailPlacementTemplateFormValues {
  setValue: UseFormReturn['setValue']
  watchAll: { toEmails: string }
  handleEmailSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>
  emailIsSending: boolean
}

export enum EmailPlacementTemplateFormFields {
  EMAILS = 'toEmails'
}

export interface UseEmailPlacementTemplateFormArgs extends GeneratePlacementTemplateWithFilesProps {
  onClose: () => void
  templateId: string
}

export interface EmailPlacementTemplateFormProps extends UseEmailPlacementTemplateFormArgs {
  flowcode: FcSvg
}

export interface WirePlacementTemplateModalProps extends EmailPlacementTemplateFormProps {
  open: boolean
}
